import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { costApi, userApi } from '../../api';
import { PageHeader, Table, Pagination, Modal, FormItem, message } from '../../components/common';
import './CostManagement.css';

const CostManagement = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, per_page: 10, total: 0 });
  const [filters, setFilters] = useState({
    cost_type: '',
    channel_id: '',
    status: '',
    manager_id: '',
    keyword: ''
  });
  const [sort, setSort] = useState({ field: 'created_at', order: 'desc' });

  // 模态框状态
  const [modalVisible, setModalVisible] = useState(false);
  const [channelModalVisible, setChannelModalVisible] = useState(false);
  const [ruleModalVisible, setRuleModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);

  // 表单数据
  const [formData, setFormData] = useState({
    cost_type: 'expense',
    channel_id: '',
    manager_id: '',
    book_amount: '',
    actual_amount: '',
    rule_id: '',
    remark: '',
    attachments: []
  });

  // 图片预览
  const [previewImage, setPreviewImage] = useState(null);
  const fileInputRef = useRef(null);

  // 下拉选项
  const [channels, setChannels] = useState([]);
  const [rules, setRules] = useState([]);
  const [managers, setManagers] = useState([]);

  useEffect(() => {
    fetchChannels();
    fetchRules();
    fetchManagers();
  }, []);

  useEffect(() => {
    fetchRecords();
  }, [pagination.page, pagination.per_page, filters, sort]);

  const fetchRecords = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        per_page: pagination.per_page,
        sort_by: sort.field,
        order: sort.order,
        ...filters
      };
      const res = await costApi.getRecords(params);
      setRecords(res.data.items);
      setPagination(prev => ({ ...prev, total: res.data.total }));
    } catch (error) {
      message.error('获取记录失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchChannels = async () => {
    try {
      const res = await costApi.getChannels();
      setChannels(res.data);
    } catch (error) {
      console.error('获取渠道失败', error);
    }
  };

  const fetchRules = async () => {
    try {
      const res = await costApi.getRules({});
      setRules(res.data);
    } catch (error) {
      console.error('获取规则失败', error);
    }
  };

  const fetchManagers = async () => {
    try {
      const res = await userApi.getList({ role: 'admin' });
      console.log('获取到的管理员列表:', res.data);
      setManagers(res.data.items || []);
    } catch (error) {
      console.error('获取管理员失败', error);
    }
  };

  const handleCreate = () => {
    console.log('当前用户:', user);
    console.log('管理员列表:', managers);
    setEditingRecord(null);

    // 获取支出类型的第一个规则作为默认值
    const expenseRules = rules.filter(r => r.cost_type === 'expense' && r.is_active);
    const defaultRuleId = expenseRules.length > 0 ? expenseRules[0].id : '';

    // 获取第一个激活的渠道作为默认值
    const activeChannels = channels.filter(c => c.is_active);
    const defaultChannelId = activeChannels.length > 0 ? activeChannels[0].id : '';

    setFormData({
      cost_type: 'expense',
      channel_id: defaultChannelId,
      manager_id: user?.id || '',
      book_amount: '',
      actual_amount: '',
      rule_id: defaultRuleId,
      remark: '',
      attachments: []
    });
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormData({
      cost_type: record.cost_type,
      channel_id: record.channel_id,
      manager_id: record.manager_id,
      book_amount: record.book_amount,
      actual_amount: record.actual_amount,
      rule_id: record.rule_id || '',
      remark: record.remark || '',
      attachments: record.attachments || []
    });
    setModalVisible(true);
  };

  const handleDelete = async (record) => {
    if (!window.confirm(`确定要删除这条记录吗？`)) return;

    try {
      await costApi.deleteRecord(record.id);
      message.success('删除成功');
      fetchRecords();
    } catch (error) {
      message.error(error.message || '删除失败');
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      if (!file.type.startsWith('image/')) {
        message.error(`${file.name} 不是图片文件`);
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        message.error(`${file.name} 超过5MB限制`);
        return;
      }
      const reader = new FileReader();
      reader.onload = (ev) => {
        setFormData(prev => ({
          ...prev,
          attachments: [...prev.attachments, { file_name: file.name, file_data: ev.target.result }]
        }));
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  const handleRemoveAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async () => {
    // 验证
    if (!formData.channel_id) {
      message.error('请选择渠道');
      return;
    }
    if (!formData.manager_id) {
      message.error('请选择负责人');
      return;
    }
    if (!formData.book_amount || formData.book_amount <= 0) {
      message.error('请输入有效的账面金额');
      return;
    }

    // 如果实际金额为空，使用账面金额
    const submitData = {
      ...formData,
      actual_amount: formData.actual_amount || formData.book_amount
    };

    // 验证实际金额
    if (!submitData.actual_amount || submitData.actual_amount <= 0) {
      message.error('请输入有效的实际金额');
      return;
    }

    try {
      if (editingRecord) {
        await costApi.updateRecord(editingRecord.id, submitData);
        message.success('更新成功');
      } else {
        await costApi.createRecord(submitData);
        message.success('创建成功');
      }
      setModalVisible(false);
      fetchRecords();
    } catch (error) {
      message.error(error.message || '操作失败');
    }
  };

  const handleSort = (field) => {
    setSort(prev => ({
      field,
      order: prev.field === field && prev.order === 'desc' ? 'asc' : 'desc'
    }));
  };

  const handleSettlement = () => {
    navigate('/admin/cost-settlement');
  };

  // 根据成本类型过滤规则
  const filteredRules = rules.filter(r => r.cost_type === formData.cost_type && r.is_active);

  const columns = [
    { key: 'id', title: 'ID', width: '60px', sorter: true },
    {
      key: 'cost_type',
      title: '类型',
      width: '80px',
      render: (value) => (
        <span className={`cost-type-badge ${value}`}>
          {value === 'income' ? '收入' : '支出'}
        </span>
      )
    },
    { key: 'channel_name', title: '渠道', width: '100px' },
    {
      key: 'status',
      title: '状态',
      width: '80px',
      render: (value) => (
        <span className={`status-badge ${value}`}>
          {value === 'pending' ? '待结算' : '已结算'}
        </span>
      )
    },
    { key: 'manager_name', title: '负责人', width: '100px' },
    {
      key: 'book_amount',
      title: '账面金额',
      width: '100px',
      render: (value) => `¥${value.toFixed(2)}`
    },
    {
      key: 'actual_amount',
      title: '实际金额',
      width: '100px',
      render: (value) => `¥${value.toFixed(2)}`
    },
    { key: 'rule_name', title: '成本规则', width: '120px', render: (v) => v || '平分' },
    { key: 'created_at', title: '录入时间', width: '160px', sorter: true },
    { key: 'created_by_name', title: '录入人', width: '100px' },
    {
      key: 'actions',
      title: '操作',
      width: '120px',
      render: (_, row) => (
        <div className="action-buttons">
          {row.status === 'pending' && (
            <>
              <button onClick={() => handleEdit(row)} className="btn-edit">编辑</button>
              <button onClick={() => handleDelete(row)} className="btn-delete">删除</button>
            </>
          )}
          {row.status === 'settled' && (
            <span style={{ color: '#95a5a6' }}>已结算</span>
          )}
        </div>
      )
    }
  ];

  return (
    <div className="cost-management">
      <PageHeader
        title="成本管理"
        extra={
          <div className="header-actions">
            <button onClick={handleCreate} className="btn-primary">新建记录</button>
            <button onClick={() => setChannelModalVisible(true)} className="btn-secondary">渠道管理</button>
            <button onClick={() => setRuleModalVisible(true)} className="btn-secondary">规则管理</button>
            <button onClick={handleSettlement} className="btn-success">一键结算</button>
          </div>
        }
      />

      {/* 筛选区域 */}
      <div className="filters">
        <select
          value={filters.cost_type}
          onChange={(e) => setFilters({ ...filters, cost_type: e.target.value })}
        >
          <option value="">全部类型</option>
          <option value="income">收入</option>
          <option value="expense">支出</option>
        </select>

        <select
          value={filters.channel_id}
          onChange={(e) => setFilters({ ...filters, channel_id: e.target.value })}
        >
          <option value="">全部渠道</option>
          {channels.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>

        <select
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
        >
          <option value="">全部状态</option>
          <option value="pending">待结算</option>
          <option value="settled">已结算</option>
        </select>

        <select
          value={filters.manager_id}
          onChange={(e) => setFilters({ ...filters, manager_id: e.target.value })}
        >
          <option value="">全部负责人</option>
          {managers.map(m => (
            <option key={m.id} value={m.id}>{m.real_name || m.username}</option>
          ))}
        </select>

        <input
          type="text"
          placeholder="搜索备注..."
          value={filters.keyword}
          onChange={(e) => setFilters({ ...filters, keyword: e.target.value })}
        />
      </div>

      {/* 数据表格 */}
      <Table
        columns={columns}
        data={records}
        loading={loading}
        onSort={handleSort}
        sortField={sort.field}
        sortOrder={sort.order}
      />

      {/* 分页 */}
      <Pagination
        current={pagination.page}
        pageSize={pagination.per_page}
        total={pagination.total}
        onChange={(page) => setPagination({ ...pagination, page })}
        onPageSizeChange={(per_page) => setPagination({ ...pagination, page: 1, per_page })}
      />

      {/* 新建/编辑模态框 */}
      <Modal
        visible={modalVisible}
        title={editingRecord ? '编辑成本记录' : '新建成本记录'}
        onClose={() => setModalVisible(false)}
        onConfirm={handleSubmit}
      >
        <FormItem label="成本类型" required>
          <select
            value={formData.cost_type}
            onChange={(e) => {
              const newType = e.target.value;
              const typeRules = rules.filter(r => r.cost_type === newType && r.is_active);
              const defaultRuleId = typeRules.length > 0 ? typeRules[0].id : '';
              setFormData({ ...formData, cost_type: newType, rule_id: defaultRuleId });
            }}
          >
            <option value="expense">支出</option>
            <option value="income">收入</option>
          </select>
        </FormItem>

        <FormItem label="渠道" required>
          <select
            value={formData.channel_id}
            onChange={(e) => setFormData({ ...formData, channel_id: e.target.value })}
          >
            <option value="">请选择渠道</option>
            {channels.filter(c => c.is_active).map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </FormItem>

        <FormItem label="负责人" required>
          <select
            value={formData.manager_id}
            onChange={(e) => setFormData({ ...formData, manager_id: e.target.value })}
          >
            <option value="">请选择负责人</option>
            {managers.map(m => (
              <option key={m.id} value={m.id}>{m.real_name || m.username}</option>
            ))}
          </select>
        </FormItem>

        <FormItem label="账面金额" required>
          <input
            type="number"
            step="0.01"
            value={formData.book_amount}
            onChange={(e) => setFormData({ ...formData, book_amount: e.target.value })}
            placeholder="请输入账面金额"
          />
        </FormItem>

        <FormItem label="实际金额" required>
          <input
            type="number"
            step="0.01"
            value={formData.actual_amount}
            onChange={(e) => setFormData({ ...formData, actual_amount: e.target.value })}
            placeholder={formData.book_amount ? `默认使用账面金额：¥${formData.book_amount}` : '请输入实际金额（不填则使用账面金额）'}
          />
          {formData.book_amount && !formData.actual_amount && (
            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
              💡 未填写时将使用账面金额：¥{formData.book_amount}
            </div>
          )}
        </FormItem>

        <FormItem label="成本规则">
          <select
            value={formData.rule_id}
            onChange={(e) => setFormData({ ...formData, rule_id: e.target.value })}
          >
            <option value="">请选择规则</option>
            {filteredRules.map(r => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </select>
        </FormItem>

        <FormItem label="备注">
          <textarea
            value={formData.remark}
            onChange={(e) => setFormData({ ...formData, remark: e.target.value })}
            placeholder="请输入备注"
            rows={3}
          />
        </FormItem>

        <FormItem label="图片附件">
          <div className="attachment-section">
            <input
              type="file"
              ref={fileInputRef}
              accept="image/*"
              multiple
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            <button
              type="button"
              className="btn-secondary"
              onClick={() => fileInputRef.current?.click()}
              style={{ marginBottom: '8px' }}
            >
              选择图片
            </button>
            {formData.attachments.length > 0 && (
              <div className="attachment-list">
                {formData.attachments.map((att, index) => (
                  <div key={index} className="attachment-item">
                    <img
                      src={att.file_data}
                      alt={att.file_name}
                      onClick={() => setPreviewImage(att.file_data)}
                    />
                    <button
                      type="button"
                      className="attachment-remove"
                      onClick={() => handleRemoveAttachment(index)}
                    >×</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </FormItem>
      </Modal>

      {/* 渠道管理模态框 */}
      {channelModalVisible && (
        <ChannelManagementModal
          visible={channelModalVisible}
          onClose={() => {
            setChannelModalVisible(false);
            fetchChannels();
          }}
        />
      )}

      {/* 规则管理模态框 */}
      {ruleModalVisible && (
        <RuleManagementModal
          visible={ruleModalVisible}
          onClose={() => {
            setRuleModalVisible(false);
            fetchRules();
          }}
        />
      )}

      {/* 图片预览 */}
      {previewImage && (
        <div className="image-preview-overlay" onClick={() => setPreviewImage(null)}>
          <img src={previewImage} alt="预览" />
        </div>
      )}
    </div>
  );
};

// 渠道管理模态框组件
const ChannelManagementModal = ({ visible, onClose }) => {
  const [channels, setChannels] = useState([]);
  const [editingChannel, setEditingChannel] = useState(null);
  const [channelName, setChannelName] = useState('');

  useEffect(() => {
    if (visible) {
      fetchChannels();
    }
  }, [visible]);

  const fetchChannels = async () => {
    try {
      const res = await costApi.getChannels();
      setChannels(res.data);
    } catch (error) {
      message.error('获取渠道失败');
    }
  };

  const handleAdd = async () => {
    if (!channelName.trim()) {
      message.error('请输入渠道名称');
      return;
    }

    try {
      await costApi.createChannel({ name: channelName });
      message.success('添加成功');
      setChannelName('');
      fetchChannels();
    } catch (error) {
      message.error(error.message || '添加失败');
    }
  };

  const handleUpdate = async (channel) => {
    if (!channelName.trim()) {
      message.error('请输入渠道名称');
      return;
    }

    try {
      await costApi.updateChannel(channel.id, { name: channelName });
      message.success('更新成功');
      setEditingChannel(null);
      setChannelName('');
      fetchChannels();
    } catch (error) {
      message.error(error.message || '更新失败');
    }
  };

  return (
    <Modal
      visible={visible}
      title="渠道管理"
      onClose={onClose}
      width="500px"
    >
      <div className="channel-management">
        <div className="add-channel">
          <input
            type="text"
            value={channelName}
            onChange={(e) => setChannelName(e.target.value)}
            placeholder="输入渠道名称"
          />
          {editingChannel ? (
            <>
              <button onClick={() => handleUpdate(editingChannel)} className="btn-primary">更新</button>
              <button onClick={() => { setEditingChannel(null); setChannelName(''); }} className="btn-secondary">取消</button>
            </>
          ) : (
            <button onClick={handleAdd} className="btn-primary">添加</button>
          )}
        </div>

        <div className="channel-list">
          {channels.map(channel => (
            <div key={channel.id} className="channel-item">
              <span>{channel.name}</span>
              <button
                onClick={() => {
                  setEditingChannel(channel);
                  setChannelName(channel.name);
                }}
                className="btn-edit"
              >
                编辑
              </button>
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
};

// 规则管理模态框组件
const RuleManagementModal = ({ visible, onClose }) => {
  const [activeTab, setActiveTab] = useState('expense');
  const [rules, setRules] = useState([]);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [ruleForm, setRuleForm] = useState({
    name: '',
    cost_type: 'expense',
    rule_type: 'equal_split',
    bonus_percent: 2,
    description: ''
  });

  useEffect(() => {
    if (visible) {
      fetchRules();
    }
  }, [visible, activeTab]);

  const fetchRules = async () => {
    try {
      const res = await costApi.getRules({ cost_type: activeTab });
      setRules(res.data);
    } catch (error) {
      message.error('获取规则失败');
    }
  };

  const handleAddRule = () => {
    setEditingRule(null);
    setRuleForm({
      name: '',
      cost_type: activeTab,
      rule_type: 'equal_split',
      bonus_percent: 2,
      description: ''
    });
    setEditModalVisible(true);
  };

  const handleEditRule = (rule) => {
    setEditingRule(rule);
    setRuleForm({
      name: rule.name,
      cost_type: rule.cost_type,
      rule_type: rule.rule_type,
      bonus_percent: rule.rule_config?.bonus_percent || 2,
      description: rule.description || ''
    });
    setEditModalVisible(true);
  };

  const handleSubmitRule = async () => {
    if (!ruleForm.name.trim()) {
      message.error('请输入规则名称');
      return;
    }

    try {
      if (editingRule) {
        await costApi.updateRule(editingRule.id, ruleForm);
        message.success('更新成功');
      } else {
        await costApi.createRule(ruleForm);
        message.success('创建成功');
      }
      setEditModalVisible(false);
      fetchRules();
    } catch (error) {
      message.error(error.message || '操作失败');
    }
  };

  return (
    <Modal
      visible={visible}
      title="规则管理"
      onClose={onClose}
      width="600px"
    >
      <div className="rule-management">
        <div className="tabs">
          <button
            className={activeTab === 'expense' ? 'active' : ''}
            onClick={() => setActiveTab('expense')}
          >
            支出规则
          </button>
          <button
            className={activeTab === 'income' ? 'active' : ''}
            onClick={() => setActiveTab('income')}
          >
            收入规则
          </button>
        </div>

        <button onClick={handleAddRule} className="btn-primary" style={{ marginBottom: '10px' }}>
          新建规则
        </button>

        <div className="rule-list">
          {rules.map(rule => (
            <div key={rule.id} className="rule-item">
              <div className="rule-info">
                <h4>{rule.name}</h4>
                <p>{rule.description}</p>
                <span className="rule-type">
                  {rule.rule_type === 'equal_split' ? '平分' : `负责人奖励${rule.rule_config?.bonus_percent || 0}%`}
                </span>
              </div>
              <button onClick={() => handleEditRule(rule)} className="btn-edit">编辑</button>
            </div>
          ))}
        </div>
      </div>

      {/* 编辑规则模态框 */}
      <Modal
        visible={editModalVisible}
        title={editingRule ? '编辑规则' : '新建规则'}
        onClose={() => setEditModalVisible(false)}
        onConfirm={handleSubmitRule}
      >
        <FormItem label="规则名称" required>
          <input
            type="text"
            value={ruleForm.name}
            onChange={(e) => setRuleForm({ ...ruleForm, name: e.target.value })}
            placeholder="请输入规则名称"
          />
        </FormItem>

        <FormItem label="规则类型" required>
          <select
            value={ruleForm.rule_type}
            onChange={(e) => setRuleForm({ ...ruleForm, rule_type: e.target.value })}
          >
            <option value="equal_split">平分</option>
            <option value="manager_bonus">负责人奖励</option>
          </select>
        </FormItem>

        {ruleForm.rule_type === 'manager_bonus' && (
          <FormItem label="奖励百分比" required>
            <input
              type="number"
              step="0.1"
              value={ruleForm.bonus_percent}
              onChange={(e) => setRuleForm({ ...ruleForm, bonus_percent: parseFloat(e.target.value) })}
              placeholder="请输入奖励百分比"
            />
          </FormItem>
        )}

        <FormItem label="规则描述">
          <textarea
            value={ruleForm.description}
            onChange={(e) => setRuleForm({ ...ruleForm, description: e.target.value })}
            placeholder="请输入规则描述"
            rows={3}
          />
        </FormItem>
      </Modal>
    </Modal>
  );
};

export default CostManagement;
